import React, { createContext, useContext, useState, useCallback } from 'react';

export interface LMSLead {
  id: string;
  brand_id: string;
  course_id: string;
  type: string;
  email: string;
  name: string | null;
  phone: string | null;
  notes: string | null;
  activity: string | null;
  metadata: Record<string, any> | null;
  status: string | null;
  created_at: string;
  updated_at: string;
  course?: {
    id: string;
    title: string;
  };
}

export interface LMSLeadFormData {
  course_id: string;
  email: string;
  name: string;
  phone: string;
  notes: string;
  activity: string;
  type: string;
  status: string;
}

type DialogMode = 'add' | 'edit';

interface LMSLeadDialogContextType {
  isOpen: boolean;
  mode: DialogMode;
  lead: LMSLead | null;
  formData: LMSLeadFormData;
  openAdd: () => void;
  openEdit: (lead: LMSLead) => void;
  closeDialog: () => void;
  updateFormData: (data: Partial<LMSLeadFormData>) => void;
  resetForm: () => void;
  onSuccess?: () => void;
  setOnSuccess: (callback: (() => void) | undefined) => void;
}

const defaultFormData: LMSLeadFormData = {
  course_id: '',
  email: '',
  name: '',
  phone: '',
  notes: '',
  activity: '',
  type: 'waiting_list',
  status: 'pending',
};

const LMSLeadDialogContext = createContext<LMSLeadDialogContextType | undefined>(undefined);

export const LMSLeadDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<DialogMode>('add');
  const [lead, setLead] = useState<LMSLead | null>(null);
  const [formData, setFormData] = useState<LMSLeadFormData>(defaultFormData);
  const [onSuccess, setOnSuccess] = useState<(() => void) | undefined>(undefined);

  const resetForm = useCallback(() => {
    setFormData(defaultFormData);
  }, []);

  const openAdd = useCallback(() => {
    resetForm();
    setLead(null);
    setMode('add');
    setIsOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((l: LMSLead) => {
    setLead(l);
    setFormData({
      course_id: l.course_id,
      email: l.email,
      name: l.name || '',
      phone: l.phone || '',
      notes: l.notes || '',
      activity: l.activity || '',
      type: l.type,
      status: l.status || 'pending',
    });
    setMode('edit');
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    // Don't reset form immediately to avoid flicker during close animation
    setTimeout(() => {
      setLead(null);
      resetForm();
    }, 200);
  }, [resetForm]);

  const updateFormData = useCallback((data: Partial<LMSLeadFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  return (
    <LMSLeadDialogContext.Provider
      value={{
        isOpen,
        mode,
        lead,
        formData,
        openAdd,
        openEdit,
        closeDialog,
        updateFormData,
        resetForm,
        onSuccess,
        setOnSuccess,
      }}
    >
      {children}
    </LMSLeadDialogContext.Provider>
  );
};

export const useLMSLeadDialog = () => {
  const context = useContext(LMSLeadDialogContext);
  if (context === undefined) {
    throw new Error('useLMSLeadDialog must be used within a LMSLeadDialogProvider');
  }
  return context;
};
